import EuSignNativeService from '@/mobile/eusign-native.service';
import { IDeviceReadedKeyInfo, IKeyMedia } from '@/models/eusign-native.models';
import { EndUserHashAlgo, PrivateKeyType, SignMethod } from '@/sign/models/sign-constants';
import { euSignService } from '@/sign/services/eusign.service';

export class EuSignNativeProxyService {
  private readedKeyInfo?: IDeviceReadedKeyInfo;

  public GetKeyMedias(): Promise<Array<any>> {
    return EuSignNativeService.findDevices();
  }

  public IsPrivateKeyReaded(): Promise<boolean> {
    return EuSignNativeService.isPrivateKeyReaded();
  }

  public async ReadPrivateKey(keyMedia: IKeyMedia, certs?: Array<Uint8Array>, caCN?: string): Promise<any> {
    const owner = await EuSignNativeService.readKey(keyMedia);
    this.resetReadedKeyInfo();
    if (owner) {
      this.readedKeyInfo = { ...keyMedia, ...{ serial: owner.serial } };
    }
    return owner;
  }

  public async CtxReadPrivateKey(privateKeyContext: any, keyMedia: IKeyMedia, certs?: Array<Uint8Array>, caCN?: string): Promise<any> {
    const ownerInfo = await this.ReadPrivateKey(keyMedia, certs, caCN);
    return { ownerInfo };
  }

  public ResetPrivateKey(): Promise<any> {
    if (this.readedKeyInfo) {
      this.resetReadedKeyInfo();
      return EuSignNativeService.resetPrivateKey();
    } else {
      return Promise.resolve();
    }
  }

  public CtxFreePrivateKey(privateKeyContext: any): Promise<any> {
    return this.ResetPrivateKey();
  }

  public GetOwnCertificates(): Promise<Array<any>> {
    return EuSignNativeService.getCertificate();
  }

  public CtxGetOwnCertificates(privateKeyContext: any): Promise<Array<any>> {
    return this.GetOwnCertificates();
  }

  public SignHash(
    signAlgo: any,
    hash: string,
    appendCert: boolean,
    asBase64String?: boolean): Promise<Uint8Array | string> {
    return EuSignNativeService.signHash(hash, this.readedKeyInfo);
  }

  public CtxSignHash(
    privateKeyContext: any,
    signAlgo: any,
    hash: string,
    appendCert: boolean,
    asBase64String?: boolean): Promise<Uint8Array | string> {
    return this.SignHash(signAlgo, hash, appendCert, asBase64String);
  }

  public SignDataEx(
    signAlgo: any,
    data: Uint8Array | string,
    external: boolean,
    appendCert: boolean,
    asBase64String?: boolean): Promise<Uint8Array | string> {
    return EuSignNativeService.signData(data, this.readedKeyInfo);
  }

  public CtxSignData(
    privateKeyContext: any,
    signAlgo: any,
    data: Uint8Array | string,
    external: boolean,
    appendCert: boolean,
    asBase64String?: boolean): Promise<Uint8Array | string> {
    return this.SignDataEx(signAlgo, data, external, appendCert, asBase64String);
  }

  public CtxCreate(): Promise<any> {
    return Promise.resolve({});
  }

  public CtxFree(privateKeyContext: any): Promise<void> {
    return Promise.resolve();
  }

  public async HashData(hashAlgo: EndUserHashAlgo | number, data: string | Uint8Array | Array<string | Uint8Array>,
    asBase64String: boolean): Promise<string | Uint8Array | Array<string | Uint8Array>> {
    let lib = euSignService.getLibrary(SignMethod.FileSystem, PrivateKeyType.Key);

    if (!lib) {
      await euSignService.init(SignMethod.FileSystem, PrivateKeyType.Key);
    }

    lib = euSignService.getLibrary(SignMethod.FileSystem, PrivateKeyType.Key);

    return lib.HashData(hashAlgo, data, asBase64String);
  }

  private resetReadedKeyInfo() {
    this.readedKeyInfo = undefined;
  }
}
